import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FiBell, FiClock } from 'react-icons/fi'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useAuthStore } from '../store'
import { connectRealtime, subscribeRealtime } from '../services/realtime'

const REALTIME_ENABLED = import.meta.env.VITE_ENABLE_ALERTS_REALTIME !== 'false'

const DEFAULT_POLL_INTERVAL_MS = 30000
const DEFAULT_LIST_LIMIT = 5

function parseCount(value) {
  const n = parseInt(value, 10)
  return Number.isFinite(n) ? n : 0
}

function formatDateTime(value) {
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return ''
  }
}

function severityDotClass(severity) {
  switch (severity) {
    case 'critical':
      return 'bg-red-600'
    case 'error':
      return 'bg-orange-500'
    case 'warning':
      return 'bg-yellow-500'
    case 'info':
    default:
      return 'bg-blue-500'
  }
}

export default function AlertBell({
  onCountChange,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  listLimit = DEFAULT_LIST_LIMIT
}) {
  const navigate = useNavigate()
  const { token, tenant } = useAuthStore()
  const containerRef = useRef(null)
  const prevCountRef = useRef(null)
  const intervalRef = useRef(null)
  const isOpenRef = useRef(false)

  const [count, setCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loadingList, setLoadingList] = useState(false)
  const [alerts, setAlerts] = useState([])

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get('/api/alerts/stats')
      const nextCount = parseCount(res.data?.pendentes)

      setCount(nextCount)
      if (typeof onCountChange === 'function') {
        onCountChange(nextCount)
      }

      const prev = prevCountRef.current
      const shouldToast = prev !== null && nextCount > prev && !isOpen
      if (shouldToast) {
        toast.success(`Novo(s) alerta(s)! Voce tem ${nextCount} pendente(s).`, {
          duration: 5000
        })
      }

      prevCountRef.current = nextCount
    } catch (error) {
      console.error('Erro ao buscar contagem de alertas:', error)
    }
  }, [isOpen, onCountChange])

  const fetchList = useCallback(async () => {
    setLoadingList(true)
    try {
      const res = await api.get('/api/alerts', {
        params: { status: 'pendente', limit: listLimit, offset: 0 }
      })
      setAlerts(res.data?.alertas || [])
    } catch (error) {
      console.error('Erro ao buscar lista de alertas:', error)
    } finally {
      setLoadingList(false)
    }
  }, [listLimit])

  // Realtime (Socket.IO)
  useEffect(() => {
    if (!REALTIME_ENABLED) return
    if (!token || !tenant?.id) return

    connectRealtime({ token, tenantId: tenant.id })

    const unsubscribe = subscribeRealtime('alert', (alert) => {
      const titulo = alert?.titulo || 'Novo alerta'
      const mensagem = alert?.mensagem || ''

      if (!isOpenRef.current) {
        toast.success(mensagem ? `${titulo}: ${mensagem}` : titulo, { duration: 5000 })
      }

      setCount((prev) => {
        const next = prev + 1
        prevCountRef.current = next
        if (typeof onCountChange === 'function') {
          onCountChange(next)
        }
        return next
      })

      if (isOpenRef.current) {
        fetchList()
      }
    })

    return unsubscribe
  }, [token, tenant?.id, fetchList, onCountChange])

  // Polling da contagem
  useEffect(() => {
    fetchCount()

    intervalRef.current = setInterval(fetchCount, pollIntervalMs)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [fetchCount, pollIntervalMs])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Carregar lista quando abrir
  useEffect(() => {
    if (isOpen) {
      fetchList()
    }
  }, [isOpen, fetchList])

  const badgeText = count > 99 ? '99+' : String(count)

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="relative text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        title={count > 0 ? `${count} alerta(s) pendente(s)` : 'Alertas'}
        aria-label="Alertas"
      >
        {count > 0 && (
          <span className="absolute -inset-1 rounded-full bg-orange-400/30 animate-alert-ping" />
        )}
        <FiBell
          className={clsx(
            'relative w-5 h-5',
            count > 0 && 'text-orange-500 animate-bell-wiggle'
          )}
        />
        {count > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {badgeText}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
            <div className="font-semibold text-sm text-gray-900 dark:text-white">Alertas</div>
            {count > 0 && (
              <div className="text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">
                {count}
              </div>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loadingList ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Carregando...</div>
            ) : alerts.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="text-sm text-gray-600 dark:text-gray-300">Nenhum alerta pendente</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Voce esta em dia.</div>
              </div>
            ) : (
              alerts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setIsOpen(false)
                    navigate('/alerts')
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <div className="flex items-start gap-3">
                    <span className={clsx('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', severityDotClass(a.severidade))} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {a.titulo}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {a.mensagem}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        <FiClock className="w-3.5 h-3.5" />
                        <span>{formatDateTime(a.criado_em)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <Link
              to="/alerts"
              onClick={() => setIsOpen(false)}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Ver todos os alertas
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
